import React, { useState, useEffect } from 'react';
import { api } from '../api';

const WA_HOLIDAYS_2027 = [
  { date: '2027-01-01', name: "New Year's Day" },
  { date: '2027-01-26', name: 'Australia Day' },
  { date: '2027-03-01', name: 'Labour Day' },
  { date: '2027-03-26', name: 'Good Friday' },
  { date: '2027-03-27', name: 'Easter Saturday' },
  { date: '2027-03-28', name: 'Easter Sunday' },
  { date: '2027-03-29', name: 'Easter Monday' },
  { date: '2027-04-25', name: 'Anzac Day' },
  { date: '2027-06-07', name: 'Western Australia Day' },
  { date: '2027-09-27', name: "King's Birthday" },
  { date: '2027-12-25', name: 'Christmas Day' },
  { date: '2027-12-26', name: 'Boxing Day' },
  { date: '2027-12-27', name: 'Boxing Day Holiday' },
];

export default function PublicHolidaysSettings() {
  const currentYear = new Date().getFullYear();
  const [year,       setYear]       = useState(currentYear);
  const [holidays,   setHolidays]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [newDate,    setNewDate]    = useState('');
  const [newName,    setNewName]    = useState('');
  const [adding,     setAdding]     = useState(false);
  const [error,      setError]      = useState('');

  async function load() {
    setLoading(true);
    try {
      const data = await api.getPublicHolidays(year);
      setHolidays(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [year]);

  async function handleAdd() {
    if (!newDate || !newName.trim()) return;
    setAdding(true); setError('');
    try {
      await api.addPublicHoliday(newDate, newName.trim());
      setNewDate(''); setNewName('');
      await load();
    } catch (err) { setError(err.message); }
    finally { setAdding(false); }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try {
      await api.deletePublicHoliday(id);
      await load();
    } catch (err) { alert(err.message); }
  }

  async function seedYear(targetYear, list) {
    if (!window.confirm(`Seed ${list.length} WA ${targetYear} public holidays?`)) return;
    setLoading(true);
    try {
      for (const h of list) {
        try { await api.addPublicHoliday(h.date, h.name); } catch (_) {}
      }
      await load();
    } finally { setLoading(false); }
  }

  const alreadyHas2027 = holidays.some(h => h.date.startsWith('2027')) ||
    year !== 2027;

  return (
    <div style={{ maxWidth: 680 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>WA Public Holidays</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
        These dates are used to skip automatic daily contract generation and mark
        holiday days throughout the app.
      </p>

      {/* Year selector */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {[currentYear, currentYear + 1].map(y => (
          <button
            key={y}
            onClick={() => setYear(y)}
            style={{
              padding: '6px 16px', borderRadius: 0, fontSize: 13, fontWeight: 600,
              border: '1.5px solid', cursor: 'pointer',
              background: year === y ? '#1A1A1A' : 'white',
              color:      year === y ? 'white'   : '#374151',
              borderColor: year === y ? '#1A1A1A' : '#e5e7eb',
            }}
          >
            {y}
          </button>
        ))}

        {/* Seed 2027 button */}
        {year === 2027 && holidays.length === 0 && (
          <button
            onClick={() => seedYear(2027, WA_HOLIDAYS_2027)}
            style={{
              padding: '6px 16px', borderRadius: 0, fontSize: 13, fontWeight: 600,
              background: '#F5C518', color: '#1A1A1A', border: 'none', cursor: 'pointer',
              marginLeft: 8,
            }}
          >
            Seed WA 2027 Holidays
          </button>
        )}
      </div>

      {/* Holiday list */}
      <div style={{
        border: '1px solid #e5e7eb', borderRadius: 0, overflow: 'hidden', marginBottom: 20,
      }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
        ) : holidays.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            No holidays recorded for {year}.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={thSt}>Date</th>
                <th style={thSt}>Day</th>
                <th style={thSt}>Holiday</th>
                <th style={{ ...thSt, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h, i) => {
                const [y, m, d] = h.date.split('-').map(Number);
                const dt  = new Date(y, m - 1, d);
                const day = dt.toLocaleDateString('en-AU', { weekday: 'short' });
                return (
                  <tr key={h.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={tdSt}>{h.date}</td>
                    <td style={{ ...tdSt, color: '#6b7280' }}>{day}</td>
                    <td style={{ ...tdSt, fontWeight: 500 }}>{h.name}</td>
                    <td style={tdSt}>
                      <button
                        onClick={() => handleDelete(h.id, h.name)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#dc2626', fontSize: 12, padding: '2px 6px',
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add new holiday */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 0, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#374151' }}>
          Add Custom Holiday
        </div>
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 0, padding: '8px 12px', fontSize: 13, color: '#dc2626', marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Date</div>
            <input
              type="date"
              value={newDate}
              onChange={e => setNewDate(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 0, fontSize: 14 }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Holiday Name</div>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="e.g. AFL Grand Final Eve"
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb',
                borderRadius: 0, fontSize: 14, boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={!newDate || !newName.trim() || adding}
            style={{
              padding: '9px 20px', background: '#1A1A1A', color: 'white',
              border: 'none', borderRadius: 0, fontSize: 14, fontWeight: 600,
              cursor: 'pointer', opacity: (!newDate || !newName.trim() || adding) ? 0.5 : 1,
            }}
          >
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

const thSt = {
  padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5,
};
const tdSt = {
  padding: '10px 12px', fontSize: 13, color: '#1A1A1A',
};
